// Procedural + draft-imported farm-world generation. Pure data (no PixiJS) so
// it can run in Node for previews/tests as well as in the browser renderer.
import { hashSeed, mulberry32, makeNoise2D } from './prng';
import { veinVariantAt, MIN_DIST01, VEIN_REGION, ORE_TYPES } from './ores';

export const TILE = 48;                 // px per tile
export const DRAFT_TARGET_W = 32768;    // imported-map target width in px (very large, explorable)

export type Terrain = 'grass' | 'water' | 'till';
export type DecorKind =
  | 'tree' | 'bush' | 'grass_tuft' | 'flowers' | 'rock' | 'mushrooms'
  | 'hillock' | 'mountain' | 'boulder'
  | 'dungeon'    // dungeon entrance (Block 6): stand on it to descend
  | 'ore_rock'   // minable node: variant = index into ORE_TYPES (map/ores.ts)
  // structures (Block: village dressing) — placed landmarks, mostly solid
  | 'well' | 'signpost' | 'scarecrow' | 'fence' | 'barrel' | 'lantern'
  // extra ground decor for a lived-in map
  | 'logs' | 'pebbles' | 'flowerpot' | 'nest' | 'haybale'
  // volcano biome decor
  | 'basalt_pillar' | 'basalt_boulder' | 'volcano_tower'
  // birch-grove shrine: a fountain centrepiece + a robed oracle NPC who offers a gold errand
  | 'fountain' | 'oracle'
  // water FX: animated ripple/wave/fish, static stone, reed
  | 'fish' | 'ripple' | 'wave' | 'water_stone' | 'reed';

export const WATER_KINDS = new Set<DecorKind>(['fish', 'ripple', 'wave', 'water_stone', 'reed']);

// Cliffs + hard scatter that block movement (Block 6: hills are impassable
// cliffs). Dungeon entrances are deliberately NOT solid — you walk onto one to
// descend. Shared by the renderer's collision grid and the gen-time validator.
// §4: bushes are no longer solid — they're small, choppable ground decor with a
// tiny/no collider, so the hero walks (and rolls) over them and can till once cleared.
export const SOLID_DECOR = new Set<DecorKind>(['tree', 'boulder', 'ore_rock', 'hillock', 'mountain',
  'well', 'scarecrow', 'fence', 'barrel', 'fountain', 'oracle',
  'basalt_pillar', 'basalt_boulder', 'volcano_tower']);   // signpost/lantern/logs/pebbles/flowerpot/nest/haybale = walkable scenery

export interface DungeonEntrance { id: string; tx: number; ty: number; }

// Trees: variant = species*2 + ab, species indexes TREE_KINDS
// ['oak_big','oak','spruce_big','spruce','birch'], ab picks the _a / _b sprite.
export const TREE_SPECIES = 6;
export const treeVariant = (species: number, ab: number) => species * 2 + ab;

/**
 * How big a tree is, from its variant — TREE_KINDS alternates big/small
 * ('oak_big','oak','spruce_big','spruce','birch_big','birch'), so every EVEN
 * species index is a big tree.
 *
 * The server pays logs by size (WOOD_BY_SIZE in backend woodcutting.config.ts)
 * and has no map of its own, so the client reports this with the chop — the same
 * trust boundary as the tile coordinates it already sends.
 */
export const treeSizeOf = (variant: number): 'small' | 'big' =>
  Math.floor(variant / 2) % 2 === 0 ? 'big' : 'small';

/** A decorative, potentially movable object placed on the map. */
export interface Decor {
  id: string;
  kind: DecorKind;
  variant: number;       // tree: species*2+ab; small decor: sprite-variant index; hill/mountain: 0
  tx: number; ty: number;
  ox: number; oy: number; // sub-tile offset (tiles) for natural scatter
}

export interface PlotSlot { index: number; tx: number; ty: number; }

/**
 * A spawned animal. Position is a STARTING point only — the client's fauna AI
 * takes over from there (map/fauna.ts). Generated from the seed so every client
 * lays the same herd down in the same places; where each one wanders to
 * afterwards is local and doesn't need to agree.
 */
export interface Animal {
  id: string;
  species: 'chicken' | 'duck' | 'swan' | 'sheep' | 'cow' | 'horse';
  variant: string;       // 'white' | 'brown' | 'grey' | 'work' | 'racing' | 'heavy' | ...
  tx: number;
  ty: number;
}

/** Biome overlay codes (parallel to `terrain`): the land stays walkable 'grass'
 *  underneath — biome only changes the ground texture, decor and mob spawns. */
export const BIOME_MEADOW = 0;
export const BIOME_VOLCANO = 1;

export interface World {
  cols: number;
  rows: number;
  terrain: Terrain[];    // length cols*rows, row-major
  biome: Uint8Array;     // length cols*rows: BIOME_* per tile (overlay, not collision)
  decor: Decor[];
  animals: Animal[];
  plots: PlotSlot[];     // length = maxPlots; first `plotCount` are active
  dungeons: DungeonEntrance[];   // Block 6: 3–5 reachable dungeon entrances
  farm: { tx: number; ty: number; cols: number; rows: number };
  oracle?: { tx: number; ty: number };   // birch-grove quest giver NPC tile, if a grove was placed
  fisherman?: { tx: number; ty: number };  // Fisherman Fin's bank tile, if the map has a shore he can stand on
  /**
   * Tiles the PLAYER has taken: ground he cleared for good, and ground he built
   * on. Nothing wild may ever be placed on one — see canHostNode below. It is not
   * seed-derived (the seed knows nothing about a player), so it is filled in after
   * generation from the server snapshot, in `hydrateHarvests`.
   */
  reserved?: Set<string>;
  /**
   * Trees the player PLANTED: decor id → the ms timestamp its acorn finishes
   * growing. Filled after generation from the farm snapshot (applyPlantedSaplings),
   * for the same reason `reserved` is — the seed knows nothing about a player.
   * A decor id in here draws as a seedling until its deadline passes.
   */
  saplings?: Map<string, number>;
}

const at = (cols: number, x: number, y: number) => y * cols + x;
const isGrass = (t: Terrain) => t === 'grass';

// ─── Collision + dungeon gen (Block 6, pure so it's testable) ─────────────────

/** 1 = impassable (water or a solid-decor footprint). Dungeon tiles stay 0. */
export function computeBlocked(cols: number, rows: number, terrain: Terrain[], decor: Decor[]): Uint8Array {
  const g = new Uint8Array(cols * rows);
  for (let i = 0; i < g.length; i++) if (terrain[i] === 'water') g[i] = 1;
  for (const d of decor) {
    if (SOLID_DECOR.has(d.kind) && d.tx >= 0 && d.ty >= 0 && d.tx < cols && d.ty < rows) g[d.ty * cols + d.tx] = 1;
  }
  return g;
}

/** BFS-reachable walkable tiles from a start, over a blocked grid (4-dir). */
function reachableFrom(cols: number, rows: number, blocked: Uint8Array, sx: number, sy: number): Uint8Array {
  const seen = new Uint8Array(cols * rows);
  const start = at(cols, sx, sy);
  if (sx < 0 || sy < 0 || sx >= cols || sy >= rows || blocked[start]) return seen;
  const q = [start]; seen[start] = 1;
  for (let h = 0; h < q.length; h++) {
    const i = q[h], x = i % cols, y = (i / cols) | 0;
    for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const j = at(cols, nx, ny);
      if (!seen[j] && !blocked[j]) { seen[j] = 1; q.push(j); }
    }
  }
  return seen;
}

/**
 * Place 6–10 hill CLUSTERS of impassable cliff decor, then 3–5 dungeon entrances
 * on walkable tiles at their edges — each guaranteed reachable from the farm
 * (BFS-validated) and never inside impassable geometry. Mutates `decor`; returns
 * the entrances. Pure (no PixiJS) so the 100-seed guarantee test can call it.
 */
export function placeHillsAndDungeons(
  cols: number, rows: number, terrain: Terrain[], decor: Decor[],
  rnd: () => number, hillNoise: (x: number, y: number) => number,
  farm: { tx: number; ty: number }, occupied: Set<number>, idFrom: number,
): DungeonEntrance[] {
  const inBounds = (x: number, y: number) => x >= 2 && y >= 2 && x < cols - 2 && y < rows - 2;
  const free = (x: number, y: number) => inBounds(x, y) && isGrass(terrain[at(cols, x, y)]) && !occupied.has(at(cols, x, y));
  let n = idFrom;

  // Hill count: the brief's 6–10 on a real (large) map, scaled down on small ones
  // so a tiny fallback world isn't paved over with cliffs. No floor — a small map
  // may get none.
  const area = cols * rows;
  const hillTarget = Math.min(6 + Math.floor(rnd() * 5), Math.floor(area / 700));
  const HILL_GAP = 8;
  const centers: { x: number; y: number }[] = [];
  const farAll = (x: number, y: number, list: { x: number; y: number }[], gap: number) =>
    list.every((p) => Math.abs(p.x - x) + Math.abs(p.y - y) >= gap);
  const nearFarm = (x: number, y: number) => Math.abs(x - farm.tx) + Math.abs(y - farm.ty) < 10;

  // Candidate centers on the highest inland noise, spaced apart.
  const cand: { x: number; y: number; h: number }[] = [];
  for (let y = 3; y < rows - 3; y++)
    for (let x = 3; x < cols - 3; x++)
      if (free(x, y) && !nearFarm(x, y)) cand.push({ x, y, h: hillNoise(x / 13, y / 13) });
  cand.sort((a, b) => b.h - a.h);
  for (const c of cand) {
    if (centers.length >= hillTarget) break;
    if (farAll(c.x, c.y, centers, HILL_GAP)) centers.push({ x: c.x, y: c.y });
  }

  // Grow each center into a proper cliff RANGE — a noise-jittered blob 5–9 tiles
  // across (was a 1–2 tile bump that read as a lone rock). A mountain crowns the
  // core; hillocks/boulders ring it with an irregular, natural rim.
  for (const c of centers) {
    const baseR = 2 + Math.floor(rnd() * 3);   // core radius 2–4 tiles
    const R = baseR + 2;
    for (let dy = -R; dy <= R; dy++)
      for (let dx = -R; dx <= R; dx++) {
        const x = c.x + dx, y = c.y + dy;
        const dd = Math.hypot(dx, dy);
        const edge = baseR * (0.7 + hillNoise(x / 5, y / 5) * 0.65);   // ragged rim
        if (dd > edge || !free(x, y)) continue;
        const kind: DecorKind = dd < 0.7 ? 'mountain' : (rnd() < 0.5 ? 'hillock' : 'boulder');
        decor.push({ id: `hl${n++}`, kind, variant: 0, tx: x, ty: y, ox: (rnd() - 0.5) * 0.2, oy: (rnd() - 0.5) * 0.2 });
        occupied.add(at(cols, x, y));
      }
  }

  // Entrances: 3–5, on a walkable tile beside a hill cluster, reachable from farm.
  const blocked = computeBlocked(cols, rows, terrain, decor);
  const fromFarm = reachableFrom(cols, rows, blocked, farm.tx, farm.ty);
  const dungeonTarget = Math.min(centers.length, 3 + Math.floor(rnd() * 3)); // 3–5
  const entrances: DungeonEntrance[] = [];
  const shuffled = [...centers].sort(() => rnd() - 0.5);

  // A cluster can fail to yield an entrance: every tile in its immediate ring may
  // be cliff, water, or cut off from the farm. One tight pass therefore under-
  // delivers on some seeds (it left maps with 2 dungeons), so widen the search
  // instead of giving up — later passes reach further out from the cluster and
  // allow entrances to sit a little closer together.
  const PASSES: { maxRing: number; minGap: number }[] = [
    { maxRing: 3, minGap: 4 },   // preferred: hugging the cliff, well spread
    { maxRing: 5, minGap: 4 },
    { maxRing: 7, minGap: 3 },   // last resort: further out, tighter spacing
  ];
  for (const { maxRing, minGap } of PASSES) {
    if (entrances.length >= dungeonTarget) break;
    for (const c of shuffled) {
      if (entrances.length >= dungeonTarget) break;
      // Skip a cluster that already has an entrance of its own.
      if (entrances.some((e) => Math.abs(e.tx - c.x) + Math.abs(e.ty - c.y) <= maxRing)) continue;
      // Look for a free, reachable tile in the ring just outside the cluster.
      const ring: [number, number][] = [];
      for (let d = 2; d <= maxRing; d++)
        for (let a = 0; a < 8; a++) {
          const ang = (a / 8) * Math.PI * 2;
          ring.push([Math.round(c.x + Math.cos(ang) * d), Math.round(c.y + Math.sin(ang) * d)]);
        }
      for (const [x, y] of ring) {
        if (!free(x, y)) continue;
        if (!fromFarm[at(cols, x, y)]) continue;   // must be reachable (the entrance IS the exit)
        if (!entrances.every((e) => Math.abs(e.tx - x) + Math.abs(e.ty - y) >= minGap)) continue;
        entrances.push({ id: `dg${entrances.length}`, tx: x, ty: y });
        occupied.add(at(cols, x, y));
        decor.push({ id: `dgd${n++}`, kind: 'dungeon', variant: 0, tx: x, ty: y, ox: 0, oy: 0 });
        break;
      }
    }
  }
  return entrances;
}

function plotBlock(maxPlots: number) {
  const cols = Math.min(6, Math.ceil(Math.sqrt(maxPlots)));
  const rows = Math.ceil(maxPlots / cols);
  return { cols, rows };
}

/**
 * Pick the farm-block origin on the most "inland" grass — far from water/sand
 * and map borders — where the whole block fits on grass. Falls back to the
 * highest-scoring cell (clamped) if nothing fits perfectly.
 */
function pickFarm(cols: number, rows: number, terrain: Terrain[], bw: number, bh: number) {
  const dist = new Int32Array(cols * rows).fill(-1);
  const q: number[] = [];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const i = at(cols, x, y);
      const border = x === 0 || y === 0 || x === cols - 1 || y === rows - 1;
      if (!isGrass(terrain[i]) || border) { dist[i] = 0; q.push(i); }
    }
  }
  for (let h = 0; h < q.length; h++) {
    const i = q[h], x = i % cols, y = (i / cols) | 0, d = dist[i];
    const nb = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]];
    for (const [nx, ny] of nb) {
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
      const j = at(cols, nx, ny);
      if (dist[j] === -1 && isGrass(terrain[j])) { dist[j] = d + 1; q.push(j); }
    }
  }
  const fits = (ox: number, oy: number) => {
    for (let y = 0; y < bh; y++) for (let x = 0; x < bw; x++)
      if (!isGrass(terrain[at(cols, ox + x, oy + y)])) return false;
    return true;
  };
  const clamp = (v: number, hi: number) => Math.max(0, Math.min(hi, v));
  let best = { tx: 0, ty: 0, score: -1 }, anyFit = false;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (dist[at(cols, x, y)] <= 0) continue;
      const ox = clamp(x - (bw >> 1), cols - bw), oy = clamp(y - (bh >> 1), rows - bh);
      const ok = fits(ox, oy);
      const score = dist[at(cols, x, y)] + (ok ? 1000 : 0);
      if ((ok && !anyFit) || score > best.score) { best = { tx: ox, ty: oy, score }; if (ok) anyFit = true; }
    }
  }
  return { tx: best.tx, ty: best.ty };
}

/** Shared assembly: place farm, till, plots, scatter forest + small decor. */
/**
 * Where Fisherman Fin stands: the best free grass tile touching water.
 *
 * Pure and exported so the choice can be tested — the generator around it needs a
 * whole world to run. Deterministic by construction (no RNG): drawing from the
 * world's stream here would shift every later draw and silently re-roll the ore,
 * the scatter and the herd on every existing seed.
 *
 * The only hard requirement is `free` + touching water, because the one outcome
 * that must not happen is Fin not existing (he was missing from the game
 * entirely until now). Everything else is a preference, expressed as a penalty
 * bigger than any Manhattan distance on the map so the tiers can never cross:
 *   1. water directly SOUTH — he is drawn facing the camera, so that is the water
 *      he is looking at;
 *   2. open water two rows south rather than the far edge of a puddle;
 *   3. dry ground behind and to the sides, i.e. a bank the player can walk up to
 *      rather than a spit poking into the lake.
 * Ties break on distance to the farm: he is a neighbour you meet early, not an
 * expedition.
 */
export function pickFishermanBank(
  cols: number, rows: number, terrain: Terrain[],
  free: (x: number, y: number) => boolean,
  farmCx: number, farmCy: number,
): { tx: number; ty: number } | undefined {
  const isWater = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < cols && y < rows && terrain[at(cols, x, y)] === 'water';
  const TIER = cols + rows;
  let bank: { tx: number; ty: number } | undefined;
  let best = Infinity;
  for (let y = 2; y < rows - 3; y++) {
    for (let x = 2; x < cols - 2; x++) {
      if (!free(x, y)) continue;
      if (!isWater(x, y + 1) && !isWater(x, y - 1) && !isWater(x - 1, y) && !isWater(x + 1, y)) continue;
      const spit = isWater(x, y - 1) || isWater(x - 1, y) || isWater(x + 1, y);
      const score = Math.abs(x - farmCx) + Math.abs(y - farmCy)
        + (isWater(x, y + 1) ? 0 : TIER * 4)
        + (isWater(x, y + 2) ? 0 : TIER * 2)
        + (spit ? TIER : 0);
      if (score < best) { best = score; bank = { tx: x, ty: y }; }
    }
  }
  return bank;
}

function assembleWorld(seedStr: string, cols: number, rows: number, terrain: Terrain[], maxPlots: number): World {
  const seed = hashSeed(seedStr || 'terraboma');
  const rnd = mulberry32(seed);
  const noise = makeNoise2D(seed ^ 0x9e3779b9);
  const w = cols;
  const block = plotBlock(maxPlots);
  const { tx: fx, ty: fy } = pickFarm(cols, rows, terrain, block.cols, block.rows);

  // The farm block stays grass; each plot is tilled into a soil bed on demand
  // (the hero hoes it), so untilled ground shows as grass framing the beds.

  const plots: PlotSlot[] = [];
  for (let i = 0; i < maxPlots; i++)
    plots.push({ index: i, tx: fx + (i % block.cols), ty: fy + Math.floor(i / block.cols) });

  const decor: Decor[] = [];
  const occupied = new Set<number>();
  let n = 0;
  const fx1 = fx + block.cols - 1, fy1 = fy + block.rows - 1;
  // Keep a clear "yard" around the garden bed. Tall decor needs a wider berth,
  // and an extra guard below the plot: a tree's canopy grows upward (north), so
  // one planted just south of the farm would overhang the beds.
  const nearFarm = (x: number, y: number, m: number) =>
    x >= fx - m && x <= fx1 + m && y >= fy - m && y <= fy1 + m;
  const canopyOverFarm = (x: number, y: number) =>
    x >= fx - 2 && x <= fx1 + 2 && y > fy1 && y <= fy1 + 5;
  const blockedForTall = (x: number, y: number) => nearFarm(x, y, 2) || canopyOverFarm(x, y);
  const nearWater = (x: number, y: number) => {
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const nx = x + dx, ny = y + dy;
      if (nx >= 0 && nx < cols && ny >= 0 && ny < rows && terrain[at(w, nx, ny)] === 'water') return true;
    }
    return false;
  };

  // Rocky uplands (Block 6): hill CLUSTERS of impassable cliff decor, with 3–5
  // reachable dungeon entrances at their edges. Reserve the farm yard + shoreline
  // first (mark them occupied) so hills/entrances never land on them, then let the
  // shared, testable placer carve the clusters and validate entrance reachability.
  const rock = makeNoise2D(seed ^ 0x27d4eb2f);
  // Own RNG so hill/dungeon placement doesn't shift the forest/fauna stream below.
  const hillRnd = mulberry32(seed ^ 0x68bc21eb);
  const reserved = new Set(occupied);
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      if (blockedForTall(x, y) || nearWater(x, y)) reserved.add(at(w, x, y));
  const dungeons = placeHillsAndDungeons(cols, rows, terrain, decor, hillRnd, rock, { tx: fx, ty: fy }, reserved, n);
  n += 4096;   // reserve an id range so later decor ids never collide with the placer's
  // Fold the hills the placer marked back into `occupied` so forest/ore/scatter
  // passes below don't drop trees on top of a cliff.
  for (const d of decor) if (SOLID_DECOR.has(d.kind) && d.kind !== 'tree' && d.kind !== 'ore_rock' && d.kind !== 'bush') occupied.add(at(w, d.tx, d.ty));
  for (const dg of dungeons) occupied.add(at(w, dg.tx, dg.ty));

  // ── Biome overlay: a VOLCANO region — a jagged blob of scorched land in the
  // corner farthest from the farm. The land stays walkable 'grass' underneath
  // (collision/nav unchanged); only the ground texture, decor and mob spawns
  // differ. Kept well clear of the farm so home always reads as a green meadow.
  const biome = new Uint8Array(cols * rows);
  {
    const bn = makeNoise2D(seed ^ 0x3c6ef372);
    const vcx = fx < cols / 2 ? Math.floor(cols * 0.80) : Math.floor(cols * 0.20);
    const vcy = fy < rows / 2 ? Math.floor(rows * 0.80) : Math.floor(rows * 0.20);
    const vrad = Math.min(cols, rows) * 0.17;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const i = at(w, x, y);
        if (terrain[i] !== 'grass' || nearFarm(x, y, 8)) continue;
        const edge = vrad * (0.65 + bn(x / 16, y / 16) * 0.6);   // noise-jittered rim
        if (Math.hypot(x - vcx, y - vcy) < edge) biome[i] = BIOME_VOLCANO;
      }
    }
  }
  const inVolcano = (x: number, y: number) => biome[at(w, x, y)] === BIOME_VOLCANO;

  // A single obsidian tower crowns the volcano — its landmark (and where a fire
  // vendor NPC will stand once that system exists). Placed on the nearest volcano
  // tile to the region centre.
  {
    const vcx0 = fx < cols / 2 ? Math.floor(cols * 0.80) : Math.floor(cols * 0.20);
    const vcy0 = fy < rows / 2 ? Math.floor(rows * 0.80) : Math.floor(rows * 0.20);
    let best = -1, bestD = Infinity;
    for (let y = 2; y < rows - 2; y++) for (let x = 2; x < cols - 2; x++) {
      const i = at(w, x, y);
      if (biome[i] !== BIOME_VOLCANO || occupied.has(i)) continue;
      const d = Math.hypot(x - vcx0, y - vcy0);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best >= 0) {
      const tx = best % cols, ty = (best / cols) | 0;
      decor.push({ id: `d${n++}`, kind: 'volcano_tower', variant: 0, tx, ty, ox: 0, oy: 0 });
      occupied.add(best);
    }
  }

  // Forest: noise-driven clumps so trees gather in natural stands. Species and
  // size vary; big oak/spruce are rarer and favour the densest stands.
  const pickTree = (dense: boolean): number => {
    const r = rnd();
    // species index into TREE_KINDS ['oak_big','oak','spruce_big','spruce'].
    // Always the `_a` variant (ab=0): the `_b` cells are shorter background
    // trees whose trunk is cropped and read as "no lower part".
    let species: number;
    if (dense && r < 0.15) species = rnd() < 0.5 ? 0 : 2;   // rare big oak / big spruce
    else if (dense && r < 0.24) species = 4;                 // occasional tall birch landmark
    else if (r < 0.42) species = 1;                          // oak
    else if (r < 0.52) species = 5;                          // scattered birch
    else species = 3;                                        // spruce
    return treeVariant(species, 0);
  };
  // Trees keep a one-tile gap from each other so their canopies don't overlap
  // into a solid mass — each tree stays a distinct, clickable object.
  const treeGap = new Set<number>();
  const markTree = (x: number, y: number) => {
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx >= 0 && ny >= 0 && nx < cols && ny < rows) treeGap.add(at(w, nx, ny));
      }
  };
  for (let y = 1; y < rows - 1; y++) {
    for (let x = 1; x < cols - 1; x++) {
      const i = at(w, x, y);
      if (!isGrass(terrain[i]) || occupied.has(i) || blockedForTall(x, y) || treeGap.has(i) || inVolcano(x, y)) continue;
      const f = noise(x / 7, y / 7);
      const p = f > 0.62 ? 0.85 : f > 0.5 ? 0.4 : 0.1; // denser attempts; spacing thins to a clean stand
      if (rnd() >= p) continue;
      decor.push({ id: `d${n++}`, kind: 'tree', variant: pickTree(f > 0.6), tx: x, ty: y, ox: (rnd() - 0.5) * 0.3, oy: (rnd() - 0.5) * 0.2 });
      occupied.add(i);
      markTree(x, y);
    }
  }

  // ── Birch grove: a dedicated stand of golden birches ringing a small clearing.
  // A fountain shrine sits at its heart with a robed oracle beside it who gives the
  // player a gold errand. Placed as a deliberate landmark: far enough from the farm
  // to be a destination, on open grass, away from water / volcano / cliffs.
  let oracle: { tx: number; ty: number } | undefined;
  {
    const GROVE_R = 6;          // grove radius (tiles)
    const CLEARING_R = 2.4;     // walkable clearing kept around the statue
    const groveClear = (cx: number, cy: number): boolean => {
      if (cx < GROVE_R + 2 || cy < GROVE_R + 2 || cx >= cols - GROVE_R - 2 || cy >= rows - GROVE_R - 2) return false;
      if (nearFarm(cx, cy, GROVE_R + 3) || inVolcano(cx, cy)) return false;
      // require open, mostly-grass, unclaimed ground across the whole footprint
      for (let dy = -GROVE_R; dy <= GROVE_R; dy++)
        for (let dx = -GROVE_R; dx <= GROVE_R; dx++) {
          const x = cx + dx, y = cy + dy, i = at(w, x, y);
          if (!isGrass(terrain[i]) || nearWater(x, y) || inVolcano(x, y)) return false;
          if (dx * dx + dy * dy <= CLEARING_R * CLEARING_R && occupied.has(i)) return false;
        }
      return true;
    };
    let cx = -1, cy = -1;
    for (let tries = 0; tries < 800 && cx < 0; tries++) {
      const x = GROVE_R + 2 + Math.floor(rnd() * (cols - 2 * GROVE_R - 4));
      const y = GROVE_R + 2 + Math.floor(rnd() * (rows - 2 * GROVE_R - 4));
      if (groveClear(x, y)) { cx = x; cy = y; }
    }
    if (cx >= 0) {
      // Ring the clearing with birches; density falls off toward the outer edge so
      // it reads as a natural stand, not a wall.
      for (let dy = -GROVE_R; dy <= GROVE_R; dy++)
        for (let dx = -GROVE_R; dx <= GROVE_R; dx++) {
          const x = cx + dx, y = cy + dy, i = at(w, x, y);
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist <= CLEARING_R + 0.5 || dist > GROVE_R) continue;   // keep the clearing open
          if (occupied.has(i) || treeGap.has(i) || !isGrass(terrain[i])) continue;
          const p = dist < GROVE_R - 2 ? 0.7 : 0.35;
          if (rnd() >= p) continue;
          const big = rnd() < 0.5;
          decor.push({ id: `bg${n++}`, kind: 'tree', variant: treeVariant(big ? 4 : 5, 0), tx: x, ty: y, ox: (rnd() - 0.5) * 0.3, oy: (rnd() - 0.5) * 0.2 });
          occupied.add(i);
          markTree(x, y);
        }
      // Fountain shrine at the centre of the clearing; the oracle stands one tile
      // south of it (facing the approach) so the player can walk up and talk.
      decor.push({ id: 'grove-fountain', kind: 'fountain', variant: 0, tx: cx, ty: cy, ox: 0, oy: 0 });
      occupied.add(at(w, cx, cy));
      const oy2 = cy + 2;
      decor.push({ id: 'grove-oracle', kind: 'oracle', variant: 0, tx: cx, ty: oy2, ox: 0, oy: 0 });
      occupied.add(at(w, cx, oy2));
      oracle = { tx: cx, ty: oy2 };
    }
  }

  // ── Fisherman Fin: the pack NPC nobody had ever put in the game.
  //
  // `NPCs (Premade)/Fisherman_Fin.png` shipped with the art and was referenced by
  // nothing. He is placed the same way the grove oracle is — a seed-derived decor
  // entity of kind 'oracle' (variant 1), which is what makes him tappable and
  // talk-able: input.ts routes walk-up-and-talk on that kind, and the renderer
  // already knows how to draw and y-sort it. A brand-new kind would have needed
  // changes in the input layer for no gain.
  //
  // FISHING IS NOT A MECHANIC IN THIS GAME. There is a `fish` action row in the
  // hero's animation set and a rod sprite, but no fishing endpoint, no catch, no
  // item — so Fin is a character with dialogue, not the front end of a system
  // that does not exist. His lines say so.
  //
  // Deterministic scan, no RNG: consuming from `rnd()` here would shift every
  // later draw and silently re-roll the ore, the scatter and the whole herd on
  // every existing seed.
  //
  // The only hard requirement is a free grass tile TOUCHING water, because the
  // one thing that must not happen is Fin not existing. Everything else is a
  // preference, expressed as a penalty larger than any distance on this map so
  // the tiers can never cross:
  //   * water directly SOUTH of him — he is drawn facing the camera, so that is
  //     the water he is looking at;
  //   * open water two rows south rather than a one-tile puddle edge;
  //   * dry ground on both sides and behind, so he stands on a bank the player
  //     can walk up to rather than on a spit poking into the lake.
  // Ties break on distance to the farm: he is meant to be a neighbour you meet
  // early, not an expedition.
  // Ore veins: minable rock nodes scattered on open inland ground, spaced apart
  // (like landmarks) so each is a distinct target. A vein now has a METAL —
  // variant is an index into ORE_TYPES (map/ores.ts) — which decides its art, the
  // pickaxe it needs, and what it drops. Weighted, so copper is the one you meet
  // on your first walk and mythril is a find.
  //
  // Veins come in CLUSTERS of ONE metal, not as an even sprinkle: a lone rock every
  // twenty tiles makes mining a chore of walking, and a mixed sprinkle means the
  // metal you need is never where you are. A cluster is a place you go TO — and it
  // has an identity, because all its veins share the metal its centre rolled.
  // The metal is seed-hashed per region (veinVariantAt) and gated by distance, so copper rings the farm and
  // mythril sits out at the map's edge — the frontier the player earns pickaxes for.
  //
  // Both this pass and the stone pass below draw from their OWN RNG streams (the
  // same trick the hill placer uses upstream): tuning a cluster's size or density
  // would otherwise shift every later draw and silently re-roll the small decor
  // and the whole herd — which is exactly how a fauna regression gets blamed on
  // the fauna pass.
  const oreRnd = mulberry32(seed ^ 0x7f4a7c15);
  const oreTileOk = (x: number, y: number) => {
    const i = at(w, x, y);
    return x >= 2 && y >= 2 && x < cols - 2 && y < rows - 2
      && isGrass(terrain[i]) && !occupied.has(i) && !treeGap.has(i)
      && !blockedForTall(x, y) && !nearWater(x, y);
  };
  // Normaliser for "how far out is this".
  //
  // This used to be the farthest CORNER of the bounding rectangle from the farm.
  // That only spans 0..1 if a vein could actually stand in a corner, and on the
  // shipped draft map it cannot: the farm lands mid-map (407,347 of 683x464) and
  // the corners are sea, map edge or blocked ground. dist01 therefore topped out
  // at 0.74 — so mythril, whose MIN_DIST01 is 0.72, had a sliver of eligible
  // ground and in practice placed ZERO veins on every seed measured. The entire
  // top rung of the ladder (mythril ore → bar → tier-6 tools/weapons, tier-5
  // armour) was unobtainable, while the tiers below it happened to work because
  // their thresholds were low enough to clear by accident.
  //
  // Now it is the farthest tile a vein is actually ALLOWED on, so dist01 really
  // does reach 1.0 and MIN_DIST01 means what its comment says: a fraction of the
  // map's own reach. Deterministic (oreTileOk consults no RNG), so this extra
  // scan does not shift the ore stream and re-roll every existing seed's decor.
  //
  // The pass also caches its own answer. oreTileOk is the expensive predicate here
  // (four set/array lookups plus two neighbourhood checks) and three later loops
  // want it for the same tiles; running it four times over a 683x486 map is a
  // second of world gen for nothing. The cache is only ever used to CHOOSE a
  // candidate — every vein is still checked against the live oreTileOk at the
  // moment it is placed, because `occupied` grows as veins go down and a stale
  // bitmap would let two of them stack on one tile.
  let farthest = 1;
  const oreOk = new Uint8Array(cols * rows);
  for (let y = 2; y < rows - 2; y++) {
    for (let x = 2; x < cols - 2; x++) {
      if (!oreTileOk(x, y)) continue;
      oreOk[at(w, x, y)] = 1;
      farthest = Math.max(farthest, Math.hypot(x - fx, y - fy));
    }
  }
  const oreNodes: { x: number; y: number }[] = [];
  const ORE_GAP = 2;                 // veins inside a cluster: adjacent-ish but each its own target
  const CLUSTER_GAP = 16;            // between cluster centres — one field per neighbourhood
  const centres: { x: number; y: number }[] = [];
  const oreClear = (x: number, y: number) => oreNodes.every((p) => Math.abs(p.x - x) + Math.abs(p.y - y) >= ORE_GAP);
  for (let y = 2; y < rows - 2; y++) {
    for (let x = 2; x < cols - 2; x++) {
      if (!oreOk[at(w, x, y)]) continue;
      // Cluster-centre roll. Raised from 0.0022 (owner: "should be a bit more of
      // the ore on the map"): at the old rate a 683x464 map grew only a handful of
      // fields, and since CLUSTER_GAP already keeps them 16 tiles apart the extra
      // rolls buy more NEIGHBOURHOODS with ore rather than a denser pile in one.
      if (oreRnd() >= 0.0038) continue;                                  // candidate cluster centre
      if (!centres.every((p) => Math.abs(p.x - x) + Math.abs(p.y - y) >= CLUSTER_GAP)) continue;
      // The metal is NOT rolled here any more: it is hashed from the seed and the
      // tile's region (ores.ts veinVariantAt), so the SERVER can derive the same
      // answer and refuse a client that claims a richer vein than it stands on.
      // The frontier is kept as a placement rule instead of a weighting — a
      // region whose metal is too rare for this distance grows no field at all.
      const variant = veinVariantAt(seed, x, y);
      const dist01 = Math.hypot(x - fx, y - fy) / (farthest || 1);
      if (dist01 < MIN_DIST01[variant]) continue;
      const rx0 = Math.floor(x / VEIN_REGION) * VEIN_REGION;
      const ry0 = Math.floor(y / VEIN_REGION) * VEIN_REGION;
      centres.push({ x, y });
      const want = 4 + Math.floor(oreRnd() * 5);                          // 4–8 veins per field
      for (let k = 0, tries = 0; k < want && tries < 24; tries++) {
        // Offsets from the centre rather than a scan, so the field is a blob and
        // the RNG stream stays a fixed, seed-stable number of draws per attempt.
        // Clamped INSIDE the centre's metal region: the region is what carries
        // the metal (the server derives it from the tile), so a vein that spilled
        // over the border would be a different metal standing in this field.
        const vx = Math.max(rx0, Math.min(rx0 + VEIN_REGION - 1, x + Math.round((oreRnd() - 0.5) * 7)));
        const vy = Math.max(ry0, Math.min(ry0 + VEIN_REGION - 1, y + Math.round((oreRnd() - 0.5) * 7)));
        if (!oreTileOk(vx, vy) || !oreClear(vx, vy)) continue;
        oreNodes.push({ x: vx, y: vy });
        decor.push({ id: `d${n++}`, kind: 'ore_rock', variant, tx: vx, ty: vy, ox: (oreRnd() - 0.5) * 0.25, oy: (oreRnd() - 0.5) * 0.2 });
        occupied.add(at(w, vx, vy));
        k++;
      }
    }
  }

  /**
   * The floor every metal is topped up to.
   *
   * OWNER: "there's no emerald and ruby ore … should be on the map, yes, maybe
   * after a super low quantity, but should be presented."
   *
   * Measured over 12 seeds before this: copper 214 veins per map, iron 111, gold
   * 40, mythril 13.6 — then emerald 7.2, amber 3.3, ruby 4.5. Never actually zero,
   * because the guarantee below already existed, but ONE field of four veins in
   * the farthest corner of a 683x464 map is not something a player finds; it is
   * something a player concludes does not exist. Which is the report.
   *
   * 8 keeps them the rarest things on the map by a wide margin (copper still has
   * ~27x more) while making a hunt for ruby a hunt rather than a rumour. The top-up
   * runs after the lottery, so a seed that was already generous keeps what it rolled.
   */
  const MIN_VEINS_PER_METAL = 8;

  // EVERY METAL MUST EXIST ON THE MAP — the frontier is a preference, not a veto.
  //
  // The scan above is a lottery: a 0.0022-per-tile centre roll, intersected with a
  // 10%-of-regions metal and the outermost band of the map. Even after dist01 was
  // fixed to span the real range, 9 of 40 measured seeds grew no mythril field at
  // all — and on those seeds mythril ore, mythril bars and the whole tier-6
  // tools / tier-5 armour rung are unobtainable, which is a dead progression wall
  // and not a difficulty setting.
  //
  // So any metal the lottery missed gets one guaranteed field, at the FARTHEST
  // eligible tile whose region already hashes to that metal. It must be a tile the
  // hash already agrees with: the server re-derives the metal from seed+tile
  // (mining/vein.math.ts) and would refuse a vein we invented anywhere else.
  // Deterministic, and it runs after every RNG draw above except its own field's,
  // so it cannot shift the stream for seeds that did not need it.
  for (let variant = 0; variant < ORE_TYPES.length; variant++) {
    let have = 0;
    for (const d of decor) if (d.kind === 'ore_rock' && d.variant === variant) have++;
    if (have >= MIN_VEINS_PER_METAL) continue;
    // Every eligible tile this metal could stand on, farthest first — the frontier
    // is a preference and the rare metals belong at the edges, but a single corner
    // field is not a resource. Spaced by CLUSTER_GAP so the top-ups are separate
    // NEIGHBOURHOODS rather than one denser pile in the same corner, which is what
    // makes a metal findable at all.
    const cand: { x: number; y: number; d: number }[] = [];
    for (let y = 2; y < rows - 2; y++) {
      for (let x = 2; x < cols - 2; x++) {
        // No oreClear() here on purpose: it is a linear scan of every vein placed
        // so far, and calling it per tile makes this search O(tiles x veins) — it
        // took world gen from ~1s to ~10s. The spacing rule is enforced where it
        // matters, on each vein as it is actually placed below.
        if (!oreOk[at(w, x, y)] || veinVariantAt(seed, x, y) !== variant) continue;
        cand.push({ x, y, d: Math.hypot(x - fx, y - fy) });
      }
    }
    if (!cand.length) continue;       // the seed hashed no region to this metal on walkable ground
    cand.sort((a, b) => b.d - a.d || a.y - b.y || a.x - b.x);   // ties broken by tile, so it stays seed-stable
    const placed: { x: number; y: number }[] = [];
    for (const c of cand) {
      if (have >= MIN_VEINS_PER_METAL) break;
      if (!placed.every((p) => Math.abs(p.x - c.x) + Math.abs(p.y - c.y) >= CLUSTER_GAP)) continue;
      placed.push({ x: c.x, y: c.y });
      const rx0 = Math.floor(c.x / VEIN_REGION) * VEIN_REGION;
      const ry0 = Math.floor(c.y / VEIN_REGION) * VEIN_REGION;
      for (let k = 0, tries = 0; k < 4 && tries < 24; tries++) {
        const vx = Math.max(rx0, Math.min(rx0 + VEIN_REGION - 1, c.x + Math.round((oreRnd() - 0.5) * 7)));
        const vy = Math.max(ry0, Math.min(ry0 + VEIN_REGION - 1, c.y + Math.round((oreRnd() - 0.5) * 7)));
        if (!oreTileOk(vx, vy) || !oreClear(vx, vy)) continue;
        oreNodes.push({ x: vx, y: vy });
        decor.push({ id: `d${n++}`, kind: 'ore_rock', variant, tx: vx, ty: vy, ox: (oreRnd() - 0.5) * 0.25, oy: (oreRnd() - 0.5) * 0.2 });
        occupied.add(at(w, vx, vy));
        k++; have++;
      }
    }
  }

  // A STARTER VEIN, within sight of the farm.
  //
  // Two reasons, one placement. The tutorial's step 8 says "click an ore vein",
  // and both the arrow and the lesson are worthless if the nearest vein is a
  // three-minute walk away — the player never learns what a vein LOOKS like on
  // the map, which is the whole point of the step. And the ore lottery above is a
  // lottery: nothing guaranteed a field anywhere near home.
  //
  // Copper only, and only on a tile whose region already hashes to copper: the
  // server re-derives a vein's metal from seed+tile (mining/vein.math.ts) and
  // would refuse one we invented. Copper is also the one metal with no frontier
  // (MIN_DIST01[0] === 0), so a field beside the farm is the rule working rather
  // than an exception carved out of it.
  //
  // Skipped when the lottery already put copper within STARTER_MAX — the point is
  // a vein near home, not a vein in this exact spot.
  const STARTER_MIN = 6;      // not IN the yard: far enough to be a walk, not decor
  const STARTER_MAX = 16;     // and close enough that the tutorial arrow is a short trip
  const COPPER = 0;
  {
    const near = oreNodes.some((p2) => Math.hypot(p2.x - fx, p2.y - fy) <= STARTER_MAX
      && veinVariantAt(seed, p2.x, p2.y) === COPPER);
    if (!near) {
      let best: { x: number; y: number; d: number } | null = null;
      for (let y = 2; y < rows - 2; y++) {
        for (let x = 2; x < cols - 2; x++) {
          if (!oreOk[at(w, x, y)] || veinVariantAt(seed, x, y) !== COPPER) continue;
          const d = Math.hypot(x - fx, y - fy);
          if (d < STARTER_MIN) continue;
          if (!best || d < best.d) best = { x, y, d };
        }
      }
      if (best) {
        const rx0 = Math.floor(best.x / VEIN_REGION) * VEIN_REGION;
        const ry0 = Math.floor(best.y / VEIN_REGION) * VEIN_REGION;
        for (let k = 0, tries = 0; k < 4 && tries < 24; tries++) {
          const vx = Math.max(rx0, Math.min(rx0 + VEIN_REGION - 1, best.x + Math.round((oreRnd() - 0.5) * 7)));
          const vy = Math.max(ry0, Math.min(ry0 + VEIN_REGION - 1, best.y + Math.round((oreRnd() - 0.5) * 7)));
          if (!oreTileOk(vx, vy) || !oreClear(vx, vy)) continue;
          oreNodes.push({ x: vx, y: vy });
          decor.push({ id: `d${n++}`, kind: 'ore_rock', variant: COPPER, tx: vx, ty: vy, ox: (oreRnd() - 0.5) * 0.25, oy: (oreRnd() - 0.5) * 0.2 });
          occupied.add(at(w, vx, vy));
          k++;
        }
      }
    }
  }

  // ── Stone fields: the map now carries all FOURTEEN pack rocks (10 ground rocks
  // as `rock`, 4 outcrops as `boulder` — scripts/build-rock-decor.py). Stone was
  // the one resource with no place to go and get it: rocks existed only as thin
  // scatter, so a stone run meant crossing the map picking up singles. A noise
  // ridge marks rocky ground, and on it rocks are dense and an outcrop occasionally
  // sits — a stone quarry you can work, with the same rules as every other node
  // (never on water, cliffs, the farm yard, the volcano, or an occupied tile).
  const stoneNoise = makeNoise2D(seed ^ 0x1b873593);
  const stoneRnd = mulberry32(seed ^ 0xcc9e2d51);
  for (let y = 1; y < rows - 1; y++) {
    for (let x = 1; x < cols - 1; x++) {
      const i = at(w, x, y);
      if (!isGrass(terrain[i]) || occupied.has(i) || nearFarm(x, y, 1) || inVolcano(x, y) || nearWater(x, y)) continue;
      const field = stoneNoise(x / 12, y / 12);
      if (field < 0.58) continue;                          // off the rocky ground entirely
      // Denser toward the middle of the field so it fades into the meadow instead
      // of ending on a hard edge.
      if (stoneRnd() >= 0.10 + (field - 0.58) * 0.9) continue;
      // An outcrop is a tall solid prop: it needs the same berth as a tree, and it
      // must not wall the yard in.
      const big = stoneRnd() < 0.10 && !blockedForTall(x, y);
      const kind: DecorKind = big ? 'boulder' : 'rock';
      decor.push({ id: `d${n++}`, kind, variant: Math.floor(stoneRnd() * (big ? 4 : 10)),
        tx: x, ty: y, ox: (stoneRnd() - 0.5) * 0.5, oy: (stoneRnd() - 0.5) * 0.4 });
      occupied.add(i);
    }
  }

  // NOTHING SOLID MAY BE WALLED IN.
  //
  // Owner: "big stones are not reachable, can't mine them." Correct, and the
  // stone fields above are why. `boulder` is SOLID_DECOR (small `rock` is not —
  // it is walkable ground decor), and in the core of a noise field the per-tile
  // chance approaches one in two, so interior boulders ended up ringed by eight
  // other solid tiles. The hero operates a node from an ADJACENT tile (reach is
  // the eight neighbours), so a fully-ringed boulder has nowhere to be worked
  // from: the tap queued a walk to a blocked tile, the walk failed, and the rock
  // simply never responded.
  //
  // Sweeping to a fixed point rather than in one pass: freeing one node opens a
  // neighbour, which can open the next, and a single sweep would leave pockets
  // two deep. Cheap in practice — the second sweep almost always finds nothing.
  {
    const solidAt = new Map<number, Decor>();
    for (const d of decor) if (SOLID_DECOR.has(d.kind)) solidAt.set(at(w, d.tx, d.ty), d);
    // A mountain blocks THREE tiles at runtime (`buildCollision` lays it out as a
    // 3-wide rect), not the one its decor entry sits on. Modelling it as one tile
    // here judged boulders on a mountain flank "reachable" at gen time and then
    // ringed them in the moment the collision grid was built.
    const flanks = new Set<number>();
    for (const d of decor) {
      if (d.kind !== 'mountain') continue;
      for (const nx of [d.tx - 1, d.tx + 1]) if (nx >= 0 && nx < cols) flanks.add(at(w, nx, d.ty));
    }
    const walled = (d: Decor): boolean => {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!dx && !dy) continue;
          const nx = d.tx + dx, ny = d.ty + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;   // the map edge is not standable
          const i = at(w, nx, ny);
          if (isGrass(terrain[i]) && !solidAt.has(i) && !flanks.has(i)) return false;   // somewhere to stand
        }
      }
      return true;
    };
    const doomed = new Set<string>();
    for (let pass = 0; pass < 8; pass++) {
      const freed: Decor[] = [];
      for (const d of solidAt.values()) {
        // Only WORKABLE NODES are thinned. A walled-in tree or cliff is scenery
        // the player never needs to reach; a boulder or an ore vein is a resource,
        // and one you cannot work is just a lie about the map. (Small `rock` is
        // not SOLID_DECOR, so it never appears in this map to begin with.)
        if (d.kind !== 'boulder' && d.kind !== 'ore_rock') continue;
        if (walled(d)) freed.push(d);
      }
      if (!freed.length) break;
      for (const d of freed) {
        doomed.add(d.id);
        solidAt.delete(at(w, d.tx, d.ty));
        occupied.delete(at(w, d.tx, d.ty));
      }
    }
    if (doomed.size) {
      for (let i = decor.length - 1; i >= 0; i--) if (doomed.has(decor[i].id)) decor.splice(i, 1);
    }
  }

  // Small decor: tufts / flowers / bush / rock / mushrooms; shore-aware. Each
  // kind gets a random sprite variant.
  const vary = (kind: DecorKind): number => {
    const counts: Partial<Record<DecorKind, number>> = {
      grass_tuft: 3, flowers: 5, rock: 10, mushrooms: 3, bush: 2, boulder: 4,
      logs: 2, pebbles: 1, flowerpot: 3, nest: 1, haybale: 1,
    };
    return Math.floor(rnd() * (counts[kind] ?? 1));
  };
  for (let y = 1; y < rows - 1; y++) {
    for (let x = 1; x < cols - 1; x++) {
      const i = at(w, x, y);
      if (!isGrass(terrain[i]) || occupied.has(i) || nearFarm(x, y, 1) || inVolcano(x, y)) continue;
      const shore = nearWater(x, y);
      // Thinned out: the map read as flooded with scatter. Lower the per-tile
      // chance so the ground breathes, and cut the "lived-in" clutter (logs /
      // pebbles / nests) down to a rare accent rather than a constant litter.
      if (rnd() >= (shore ? 0.4 : 0.15)) continue;
      const r = rnd();
      const kind: DecorKind = shore
        ? (r < 0.55 ? 'grass_tuft' : r < 0.85 ? 'rock' : 'flowers')
        : (r < 0.42 ? 'grass_tuft' : r < 0.66 ? 'flowers' : r < 0.84 ? 'bush'
          : r < 0.95 ? 'mushrooms' : r < 0.975 ? 'rock' : r < 0.99 ? 'logs' : 'pebbles');
      decor.push({ id: `d${n++}`, kind, variant: vary(kind), tx: x, ty: y, ox: (rnd() - 0.5) * 0.6, oy: (rnd() - 0.5) * 0.5 });
      occupied.add(i);
    }
  }

  // ── Volcano decor: basalt pillars & boulder piles jut from the scorched ground.
  // Sparser than the meadow's greenery so the lava veins in the floor stay visible.
  for (let y = 1; y < rows - 1; y++) {
    for (let x = 1; x < cols - 1; x++) {
      const i = at(w, x, y);
      if (!inVolcano(x, y) || occupied.has(i)) continue;
      if (rnd() >= 0.09) continue;
      const kind: DecorKind = rnd() < 0.4 ? 'basalt_pillar' : 'basalt_boulder';
      decor.push({ id: `d${n++}`, kind, variant: 0, tx: x, ty: y, ox: (rnd() - 0.5) * 0.3, oy: (rnd() - 0.5) * 0.2 });
      occupied.add(i);
    }
  }

  // ── Structures (village dressing): well, scarecrows, signposts, fences, barrels,
  // lanterns, hay bales — placed as intentional landmarks, not scatter. A ring of
  // candidate tiles around the farm gives them a "settled homestead" feel while the
  // signposts/fences wander a little further out along open ground.
  const placeOne = (kind: DecorKind, x: number, y: number): boolean => {
    if (x < 1 || y < 1 || x >= cols - 1 || y >= rows - 1) return false;
    const i = at(w, x, y);
    if (!isGrass(terrain[i]) || occupied.has(i) || nearWater(x, y)) return false;
    if (SOLID_DECOR.has(kind) && nearFarm(x, y, 2)) return false;   // keep the yard walkable
    decor.push({ id: `d${n++}`, kind, variant: vary(kind), tx: x, ty: y, ox: (rnd() - 0.5) * 0.2, oy: 0 });
    occupied.add(i);
    return true;
  };
  // Homestead dressing near the farm edge (a well + a couple of scarecrows/barrels/haybales).
  const near: DecorKind[] = ['well', 'scarecrow', 'haybale', 'flowerpot'];
  let placed = 0;
  for (let tries = 0; tries < 400 && placed < near.length; tries++) {
    const ang = rnd() * Math.PI * 2, rad = 6 + rnd() * 6;
    const x = Math.round(fx + block.cols / 2 + Math.cos(ang) * rad);
    const y = Math.round(fy + block.rows / 2 + Math.sin(ang) * rad);
    if (placeOne(near[placed], x, y)) placed++;
  }
  // Signposts + lanterns dotted across open inland ground as wayfinding landmarks.
  for (let k = 0, tries = 0; k < 11 && tries < 900; tries++) {
    const x = 2 + Math.floor(rnd() * (cols - 4)), y = 2 + Math.floor(rnd() * (rows - 4));
    if (!blockedForTall(x, y) && placeOne(rnd() < 0.45 ? 'signpost' : 'lantern', x, y)) k++;
  }
  // Fence runs (5–9 posts) along open ground — reads as old paddock lines. Posts sit
  // on ADJACENT tiles (each segment renders exactly one tile wide) so the rail runs
  // continuous instead of leaving a gap between every post.
  for (let runs = 0, tries = 0; runs < 7 && tries < 600; tries++) {
    const x0 = 3 + Math.floor(rnd() * (cols - 12)), y0 = 3 + Math.floor(rnd() * (rows - 6));
    if (nearFarm(x0, y0, 4) || blockedForTall(x0, y0)) continue;
    const len = 5 + Math.floor(rnd() * 5);
    let ok = 0;
    for (let s = 0; s < len; s++) if (placeOne('fence', x0 + s, y0)) ok++;   // adjacent tiles → seamless run
    if (ok >= 3) runs++;   // only count a real run (a lone post isn't a fence)
  }

  // Water life. The sea itself is drawn as one still sheet (see farm.ts), so all
  // of its movement comes from here — which means it has to read as weather, not
  // as wallpaper: gusts of ripple/wave glinting in patches, with calm water
  // between them, and the coastline picking up stones, reeds and fish.
  //
  // Rates are per-tile probabilities and NOT capped by scan order. A flat cap
  // hit while scanning top-down spends itself entirely on whichever water the
  // loop reaches first (on this map: ~180k open-sea tiles across the north),
  // leaving every later coast bare — the whole shore had no stones or reeds at
  // all. Shore water is rare (~6k tiles of that 180k), so it gets its own pass
  // at generous rates while the open sea is seeded sparsely.
  // Depth from the shore, in tiles: 1 = the waterline cell itself, rising out to
  // open sea. Placing by depth rather than a flat "touches land" test matters —
  // the waterline cell is mostly taken up by the lip art (see farm.ts shoreKeys),
  // so anything dropped there sits ON the rocks instead of in the water. Reeds
  // want that edge; stones and fish want the shallows just past it.
  const SHALLOW = 6;
  const depth = new Int32Array(cols * rows).fill(0);
  {
    const q: number[] = [];
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) {
      const i = at(w, x, y);
      if (terrain[i] !== 'water') continue;
      for (let dy = -1; dy <= 1 && !depth[i]; dy++) for (let dx = -1; dx <= 1 && !depth[i]; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        if (isGrass(terrain[at(w, nx, ny)])) { depth[i] = 1; q.push(i); }
      }
    }
    for (let h = 0; h < q.length; h++) {
      const i = q[h], x = i % cols, y = (i / cols) | 0, d = depth[i];
      if (d >= SHALLOW) continue;
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
        if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
        const j = at(w, nx, ny);
        if (terrain[j] === 'water' && depth[j] === 0) { depth[j] = d + 1; q.push(j); }
      }
    }
  }
  // Gust field: low-frequency noise that gathers open-sea movement into drifts
  // instead of dusting it evenly over every cell.
  const gust = makeNoise2D(seed ^ 0x5bf03635);
  const putWater = (x: number, y: number, kind: DecorKind) => decor.push({
    id: `w${n++}`, kind,
    variant: kind === 'water_stone' ? Math.floor(rnd() * 3) : 0,
    tx: x, ty: y, ox: (rnd() - 0.5) * 0.5, oy: (rnd() - 0.5) * 0.5,
  });
  for (let y = 1; y < rows - 1; y++) {
    for (let x = 1; x < cols - 1; x++) {
      const i = at(w, x, y);
      if (terrain[i] !== 'water') continue;
      const d = depth[i], r = rnd();
      if (d === 1) {                          // the waterline: reeds root here
        if (r < 0.16) putWater(x, y, 'reed');
      } else if (d === 2 || d === 3) {        // shallows: stones break the surface
        if (r < 0.14) putWater(x, y, 'water_stone');
        else if (r < 0.22) putWater(x, y, 'fish');
        else if (r < 0.30) putWater(x, y, 'ripple');
      } else if (d >= 4 && d <= SHALLOW) {    // still shallow enough for fish
        if (r < 0.06) putWater(x, y, 'fish');
        else if (r < 0.10) putWater(x, y, 'ripple');
      } else {
        // Open sea: only inside a gust, and even there only here and there, so
        // the eye reads pockets of movement against still water.
        const g = gust(x / 26, y / 26);
        if (g < 0.62) continue;               // becalmed stretches stay glassy
        const strength = (g - 0.62) / 0.38;   // 0 at a gust's edge, 1 in its heart
        if (r < 0.05 * strength) putWater(x, y, 'ripple');
        else if (r < 0.11 * strength) putWater(x, y, 'wave');
      }
    }
  }

  // ─── Fauna ─────────────────────────────────────────────────────────────────
  // Seeded, so every client lays the same herd down in the same places; where
  // each animal wanders afterwards is local and doesn't need to agree (map/fauna.ts).
  //
  // Capped per 32x32 chunk rather than globally: a flat cap on a 683-wide map
  // either starves the far side or floods the near one, and the water species
  // would crowd whichever shoreline the scan reached first — the same scan-order
  // trap that left the whole coast bare of stones (see the water FX pass above).
  const animals: Animal[] = [];
  {
    const CHUNK = 32, CAP = 6;
    const perChunk = new Map<number, number>();
    const chunkOf = (x: number, y: number) => Math.floor(y / CHUNK) * Math.ceil(cols / CHUNK) + Math.floor(x / CHUNK);
    const take = (x: number, y: number) => {
      const k = chunkOf(x, y);
      const n = perChunk.get(k) ?? 0;
      if (n >= CAP) return false;
      perChunk.set(k, n + 1);
      return true;
    };
    // Herds gather rather than dust evenly — one noise field per land species so
    // sheep and cows don't pile onto the same tiles.
    const herd = makeNoise2D(seed ^ 0x1b873593);
    const SPAWN: { species: Animal['species']; variants: string[]; water: boolean; chance: number; herdBias: number }[] = [
      { species: 'chicken', variants: ['white'], water: false, chance: 0.010, herdBias: 0.55 },
      { species: 'sheep', variants: ['white', 'brown', 'grey'], water: false, chance: 0.007, herdBias: 0.62 },
      { species: 'cow', variants: ['brown'], water: false, chance: 0.005, herdBias: 0.62 },
      { species: 'horse', variants: ['work', 'racing', 'heavy'], water: false, chance: 0.002, herdBias: 0.70 },
      { species: 'duck', variants: ['mallard'], water: true, chance: 0.05, herdBias: 0 },
      { species: 'swan', variants: ['white'], water: true, chance: 0.02, herdBias: 0 },
    ];
    // A shallow-water tile = water with grass within 2 tiles: the near-shore band
    // where ducks/swans actually paddle (not mid-ocean).
    const shallowWater = (x: number, y: number): boolean => {
      if (terrain[at(w, x, y)] !== 'water') return false;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx >= 0 && ny >= 0 && nx < cols && ny < rows && isGrass(terrain[at(w, nx, ny)])) return true;
      }
      return false;
    };
    for (let y = 2; y < rows - 2; y++) {
      for (let x = 2; x < cols - 2; x++) {
        const i = at(w, x, y);
        if (occupied.has(i)) continue;
        if (nearFarm(x, y, 4)) continue;             // don't spawn a cow in the beds (≥ the yard guard)
        const onGrass = isGrass(terrain[i]);
        const onWater = shallowWater(x, y);          // ducks/swans paddle the shallows
        const shore = onGrass && nearWater(x, y);
        if (!onGrass && !onWater) continue;          // land herds walk; water birds swim; open sea stays empty
        const r = rnd();
        let acc = 0;
        for (const sp of SPAWN) {
          if (sp.water && !onWater) continue;        // ducks/swans only on shallow water
          if (!sp.water && (!onGrass || shore)) continue;   // land herds on inland grass, off the beach
          const bias = sp.herdBias ? (herd(x / 14, y / 14) > sp.herdBias ? 3 : 0.15) : 1;
          acc += sp.chance * bias;
          if (r < acc) {
            if (!take(x, y)) break;
            animals.push({
              id: `a${n++}`,
              species: sp.species,
              variant: sp.variants[Math.floor(rnd() * sp.variants.length)],
              tx: x,
              ty: y,
            });
            occupied.add(i);
            break;
          }
        }
      }
    }
  }

  // ── Fisherman Fin, LAST of all the placement passes.
  //
  // Order is load-bearing, not taste. Every pass above draws from a seeded RNG
  // stream inside a loop that skips `occupied` tiles, so claiming one tile early
  // shifts every later draw on that stream — placing Fin before the fauna pass
  // re-rolled the entire herd, and on the small fallback map one seed came back
  // with no animals at all (fauna-spawn.test.ts caught exactly that). Running him
  // last means he can only ever cost the map the one tile he stands on.
  //
  // He also avoids tiles an animal was just seeded onto, since those are not in
  // `occupied` and a cow standing inside the fisherman is not a scene.
  // ...but that, and the yard guard, are preferences rather than vetoes. The
  // procedural FALLBACK world (used when the draft map image fails to load) is
  // only ~17x15 tiles, so a 4-tile yard guard plus a duck on the last free bank
  // left some seeds with nowhere to put him at all. Relaxing in tiers keeps the
  // big map's placement identical while guaranteeing he exists on the small one:
  // a duck he shares a beach with, or a bank a bit close to the farm, both beat
  // no fisherman.
  const animalTiles = new Set(animals.map((a) => at(w, a.tx, a.ty)));
  const bankFree = (avoidAnimals: boolean, yard: number) => (x: number, y: number) =>
    isGrass(terrain[at(w, x, y)]) && !occupied.has(at(w, x, y)) && !inVolcano(x, y) && !nearFarm(x, y, yard)
    && !(avoidAnimals && animalTiles.has(at(w, x, y)));
  const findBank = (avoidAnimals: boolean, yard: number) =>
    pickFishermanBank(cols, rows, terrain, bankFree(avoidAnimals, yard), fx + block.cols / 2, fy + block.rows / 2);
  const fisherman = findBank(true, 4) ?? findBank(false, 4) ?? findBank(false, 1);
  if (fisherman) {
    decor.push({ id: 'shore-fisherman', kind: 'oracle', variant: 1, tx: fisherman.tx, ty: fisherman.ty, ox: 0, oy: 0 });
    occupied.add(at(w, fisherman.tx, fisherman.ty));
  }

  return { cols, rows, terrain, biome, decor, animals, plots, dungeons, farm: { tx: fx, ty: fy, cols: block.cols, rows: block.rows }, oracle, fisherman, reserved: new Set<string>() };
}

/** Procedural fallback world: a grass field with a corner pond. */
export function generateWorld(seedStr: string, maxPlots: number): World {
  const seed = hashSeed(seedStr || 'terraboma');
  const rnd = mulberry32(seed);
  const noise = makeNoise2D(seed ^ 0x51ed270b);
  const block = plotBlock(maxPlots);
  const cols = block.cols + 14, rows = block.rows + 12;
  const terrain: Terrain[] = new Array(cols * rows).fill('grass');

  const cx = rnd() < 0.5 ? cols - 3.5 : 3.5, cy = rnd() < 0.5 ? 3.5 : rows - 3.5;
  const pondR = 2.2 + rnd() * 1.3;
  const midX = cols / 2, midY = rows / 2;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (Math.abs(x - midX) < cols * 0.25 && Math.abs(y - midY) < rows * 0.25) continue;
      const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) - noise(x / 3, y / 3) * 1.4;
      if (d < pondR) terrain[at(cols, x, y)] = 'water';
    }
  }
  return assembleWorld(seedStr, cols, rows, terrain, maxPlots);
}

/**
 * Classify a draft map image (RGBA) into terrain: tan/olive land → grass
 * (noise-mottled), blue-grey → water. Grass runs to the waterline; the shore
 * itself is drawn by the animated waterline autotile (see farm.ts buildScene).
 */
export function classifyDraft(data: Uint8ClampedArray | Uint8Array, imgW: number, imgH: number, cols: number, rows: number): Terrain[] {
  const terrain: Terrain[] = new Array(cols * rows);
  const sampleLand = (col: number, row: number) => {
    // Average a 3×3 grid of samples within the cell for robustness.
    let r = 0, g = 0, b = 0, c = 0;
    for (let sy = 0; sy < 3; sy++) {
      for (let sx = 0; sx < 3; sx++) {
        const px = Math.min(imgW - 1, Math.floor((col + (sx + 0.5) / 3) * imgW / cols));
        const py = Math.min(imgH - 1, Math.floor((row + (sy + 0.5) / 3) * imgH / rows));
        const o = (py * imgW + px) * 4;
        r += data[o]; g += data[o + 1]; b += data[o + 2]; c++;
      }
    }
    r /= c; g /= c; b /= c;
    // Land (warm tan/olive) has (R+G)/2 clearly above B; water (cool blue-grey) does not.
    return (r + g) / 2 - b > 16;
  };
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      terrain[at(cols, x, y)] = sampleLand(x, y) ? 'grass' : 'water';
  return terrain;
}

/** Build a world from a draft map image, sized to DRAFT_TARGET_W. */
export function generateWorldFromDraft(seedStr: string, maxPlots: number, data: Uint8ClampedArray | Uint8Array, imgW: number, imgH: number): World {
  const cols = Math.max(24, Math.round(DRAFT_TARGET_W / TILE));
  const rows = Math.max(16, Math.round(cols * imgH / imgW));
  const terrain = classifyDraft(data, imgW, imgH, cols, rows);
  return assembleWorld(seedStr, cols, rows, terrain, maxPlots);
}

// ─── Reserved ground: what the player has taken ──────────────────────────────
/**
 * Two owner reports, one rule: a tile the player has claimed is not available to
 * the world.
 *
 *   (a) "if I've destroyed the stone or tree, it will not grow there"
 *   (b) "if I've put a building, road or crafting table, there wouldn't be a tree
 *        growing on this cell"
 *
 * Both are the same question — may something wild stand here? — asked of two
 * different producers, and fixing one alone leaves the other producing exactly
 * the bug that was reported. The producers are:
 *
 *   1. WORLD GENERATION, which runs from the seed alone on every load and knows
 *      nothing about buildings placed since. It happily re-scatters a tree onto
 *      the tile your road is on, because from its point of view the road does not
 *      exist. Hence `pruneReservedDecor`, run once the farm snapshot has arrived.
 *   2. THE REGROW PASS (map/regrow-relocate.ts), which re-seeds a harvested node.
 *      It already refuses blocked/occupied tiles; the reserved set closes the case
 *      it could not see — ground that is empty precisely BECAUSE it was cleared.
 *
 * Kept here as pure set operations so both producers ask the same function, and
 * so the rule is testable without a renderer.
 */
export const tileKey = (tx: number, ty: number) => `${tx},${ty}`;

/**
 * Wild decor: nature that world-gen scatters and that may be pruned off claimed
 * ground. Deliberately NOT the landmarks (mountain, dungeon, fountain, oracle,
 * volcano_tower) — those are map furniture the player cannot build over anyway,
 * and silently deleting one would remove a quest giver or a dungeon entrance.
 */
export const WILD_KINDS = new Set<DecorKind>([
  'tree', 'bush', 'grass_tuft', 'flowers', 'rock', 'mushrooms', 'ore_rock', 'boulder',
  'logs', 'pebbles', 'nest', 'haybale', 'flowerpot', 'reed',
]);

/** A placed thing (building, road, crafting station) as the farm snapshot has it. */
export interface Placed { tx: number; ty: number; kind: string }

/**
 * Every tile the given structures stand on.
 *
 * `footprint` exists because the multi-tile kinds (the 3x2 house, the 3x1 hut)
 * cover ground their anchor tile does not: checking only `b.tx,b.ty` lets a tree
 * grow through the far end of your own cottage. The caller supplies the footprint
 * rule (the renderer already owns one, `solidBuildingTiles`) so this stays pure.
 */
export function reservedTiles(
  placed: readonly Placed[],
  footprint: (kind: string, tx: number, ty: number) => [number, number][] = (_k, tx, ty) => [[tx, ty]],
): Set<string> {
  const s = new Set<string>();
  for (const p of placed) {
    // The anchor is always taken, even for kinds whose footprint is empty because
    // they block nothing (roads, decks, torches). "Walk over it" is not "grow on it".
    s.add(tileKey(p.tx, p.ty));
    for (const [x, y] of footprint(p.kind, p.tx, p.ty)) s.add(tileKey(x, y));
  }
  return s;
}

/** May a wild node stand here? The single question both producers ask. */
export const canHostNode = (reserved: ReadonlySet<string> | undefined, tx: number, ty: number) =>
  !reserved?.has(tileKey(tx, ty));

/**
 * Drop wild decor that world generation put on claimed ground, and return what
 * was dropped.
 *
 * Mutates `world.decor` in place because the world object is shared and already
 * referenced by the caller; the return value is for tests and for the caller to
 * know whether anything downstream (buckets, collision) needs rebuilding.
 *
 * Run BEFORE the scene is built, so the sprite is never created rather than
 * created and hidden — a hidden sprite still blocks collision, which is how you
 * get an invisible wall on top of your own road.
 */
export function pruneReservedDecor(world: World, reserved: ReadonlySet<string>): Decor[] {
  if (!reserved.size) return [];
  const removed: Decor[] = [];
  const kept: Decor[] = [];
  for (const d of world.decor) {
    if (WILD_KINDS.has(d.kind) && !canHostNode(reserved, d.tx, d.ty)) removed.push(d);
    else kept.push(d);
  }
  if (removed.length) world.decor = kept;
  return removed;
}

/**
 * How far out a `regrowUntil` has to be to mean "never" rather than "not yet".
 *
 * The server stamps a permanently cleared tile with a century (woodcutting.config
 * CLEARED_FOREVER_MS) because the column is a non-null DateTime and every reader
 * already compares it against now. Thirty days is comfortably past the longest
 * real respawn timer in the game (12h) and comfortably short of a century, so the
 * two can never be confused in either direction.
 */
export const PERMANENT_CLEAR_MS = 30 * 24 * 60 * 60_000;
export const isPermanentClear = (untilMs: number, now = Date.now()) => untilMs - now > PERMANENT_CLEAR_MS;

/**
 * Harvest kinds that are OBSTACLES — things a player clears to make room, rather
 * than ground cover he tidies. Clearing one retires its tile permanently; picking
 * a mushroom does not (see regrow-relocate.ts for where the line is drawn and why).
 */
export const OBSTACLE_HARVEST_KINDS = new Set(['tree', 'ore_rock', 'stone']);

/** One server HarvestState row, as /field returns it. */
export interface HarvestRow { tx: number; ty: number; kind: string; regrowUntil: string }

/**
 * Everything the player has claimed, from the server snapshot alone.
 *
 * This is the reload path made explicit and testable: the map is re-derived from
 * the seed on every load and remembers nothing, so the ONLY memory of what the
 * player cleared or built is the snapshot. Rebuild the claim from it and the
 * cleared cell is still cleared next week.
 */
export function claimedTiles(
  placed: readonly Placed[],
  harvests: readonly HarvestRow[],
  footprint?: (kind: string, tx: number, ty: number) => [number, number][],
  now = Date.now(),
): Set<string> {
  const s = reservedTiles(placed, footprint);
  for (const h of harvests) {
    if (!OBSTACLE_HARVEST_KINDS.has(h.kind)) continue;   // renewables still come back
    const until = Date.parse(h.regrowUntil);
    if (Number.isFinite(until) && isPermanentClear(until, now)) s.add(tileKey(h.tx, h.ty));
  }
  return s;
}

// ─── Planted acorns: the player's own trees on a seed-generated map ──────────
/**
 * OWNER (2026-08-15): "from tree can fall acorn with that you can grow the trees."
 *
 * The map is regenerated from the seed on every load and remembers nothing, so a
 * planted tree lives exactly where a felled one does: in the server snapshot
 * (Farm.saplings), replayed onto the fresh decor list here — the same trick
 * hydrateHarvests plays for felled trees, from the other direction (it removes
 * what the player took; this adds what he put back).
 */
export interface SaplingRow { tx: number; ty: number; readyAt: string }

/**
 * A planted acorn always grows into a SMALL oak.
 *
 * Not a style choice: the server pays logs by size and takes the size from the
 * client's report (treeSizeOf on this variant), and its SAPLING_TREE_SIZE is
 * 'small' — a planted tree that reported 'big' would pay 3 logs for a 1-log tree
 * and turn replanting into a wood faucet. TREE_KINDS alternates big/small, so
 * species index 1 ('oak') is the small oak; treeSizeOf(2) === 'small' holds this.
 */
export const SAPLING_VARIANT = treeVariant(1, 0);

/** Decor id for the tree grown from the acorn at a tile. Derived from the TILE, not
 *  a counter, so the same row rebuilds the same id on every load — the ripeness map
 *  and the sprite cache are both keyed by it. */
export const saplingDecorId = (tx: number, ty: number) => `sap${tx},${ty}`;

/** Has this planted acorn become a real tree? The one growth rule, computed on read
 *  from the stored deadline — the server has no grown flag and no cron, and a client
 *  that cached one would show a seedling that is already choppable (or the reverse). */
export const saplingGrown = (readyAtMs: number, now = Date.now()) => readyAtMs <= now;

/**
 * Put the player's planted acorns onto a freshly generated world.
 *
 * Each row becomes one tree decor whose ripeness is remembered in `world.saplings`
 * (decor id → readyAt), so the renderer can draw a seedling or a full tree from the
 * same object. The tile is cleared of seed-scattered decor and reserved first:
 * generation runs from the seed alone, so it happily scatters an oak or a tuft onto
 * the tile the player planted, and two trees on one tile is one un-clickable blob.
 */
export function applyPlantedSaplings(world: World, rows: readonly SaplingRow[]): void {
  world.saplings = new Map<string, number>();
  if (!rows.length) return;
  const reserved = (world.reserved ??= new Set<string>());
  const tiles = new Set(rows.map((r) => tileKey(r.tx, r.ty)));
  for (const t of tiles) reserved.add(t);
  if (world.decor.some((d) => tiles.has(tileKey(d.tx, d.ty)))) {
    world.decor = world.decor.filter((d) => !tiles.has(tileKey(d.tx, d.ty)));
  }
  for (const r of rows) {
    const readyAt = Date.parse(r.readyAt);
    if (!Number.isFinite(readyAt)) continue;   // a malformed row must not plant a tree with no clock
    const id = saplingDecorId(r.tx, r.ty);
    world.saplings.set(id, readyAt);
    world.decor.push({ id, kind: 'tree', variant: SAPLING_VARIANT, tx: r.tx, ty: r.ty, ox: 0, oy: 0 });
  }
}

/**
 * Where the hero stands the first time he walks out onto the map.
 *
 * The spawn used to be the farm's doorstep, taken on faith: `(farm.tx +
 * cols / 2, farm.ty + rows + 1)`. `pickFarm` guarantees the plot BLOCK is grass;
 * it says nothing about the row underneath it, so on a seed whose farm backs onto
 * the pond the doorstep is open water and the player opened the real world
 * standing in it — the report this function exists for.
 *
 * What "somewhere to stand" actually means here is a small patch of dry ground,
 * not a single dry tile: the hero pitches a tent on his spawn, and mobs, drops
 * and the walk-out path all need room. So a spawn must be
 *
 *   - grass, and walkable (so: no water, no tree, no cliff, no building);
 *   - the middle of a clear 3x3, which is the tent's room and which by itself
 *     rules out standing on the shoreline lip; and
 *   - `WATER_CLEARANCE` tiles clear of open water, so he starts on land rather
 *     than on the beach with his back to the sea.
 *
 * Searched outward from the doorstep so the answer stays "just outside your
 * farm" whenever that tile is fine — which is the common seed. The two rules are
 * then relaxed in order (water distance, then the 3x3) rather than all at once,
 * so a cramped map degrades to the best tile it has instead of to the origin.
 * Pure, so world-spawn.test.ts can sweep it over many seeds.
 */
const SPAWN_CLEARANCE = 1;    // Chebyshev radius that must be clear → a 3x3 patch
const WATER_CLEARANCE = 2;    // no water within this many tiles of the spawn

export function pickSpawn(
  world: Pick<World, 'cols' | 'rows' | 'terrain' | 'farm'>,
  blocked: Uint8Array | null,
): { tx: number; ty: number } {
  const { cols, rows, terrain, farm } = world;
  const open = (x: number, y: number) =>
    x >= 0 && y >= 0 && x < cols && y < rows
    && isGrass(terrain[at(cols, x, y)]) && !blocked?.[at(cols, x, y)];
  const patchClear = (x: number, y: number, r: number) => {
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) if (!open(x + dx, y + dy)) return false;
    return true;
  };
  const dryAround = (x: number, y: number, r: number) => {
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;   // the map edge is not water
      if (terrain[at(cols, nx, ny)] === 'water') return false;
    }
    return true;
  };

  const ox = Math.floor(farm.tx + farm.cols / 2);
  const oy = farm.ty + farm.rows + 1;
  // Rules in order of what we give up first. Every rule keeps `open`, so a spawn
  // is never inside water or a solid — that one is not negotiable.
  const rules: ((x: number, y: number) => boolean)[] = [
    (x, y) => patchClear(x, y, SPAWN_CLEARANCE) && dryAround(x, y, WATER_CLEARANCE),
    (x, y) => patchClear(x, y, SPAWN_CLEARANCE),
    (x, y) => open(x, y),
  ];
  const maxR = Math.max(cols, rows);
  for (const ok of rules) {
    for (let r = 0; r <= maxR; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (r > 0 && Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;   // ring only
          const x = ox + dx, y = oy + dy;
          if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
          if (ok(x, y)) return { tx: x, ty: y };
        }
      }
    }
  }
  // No walkable grass anywhere — impossible for a generated world, but the farm
  // block is the one region `pickFarm` did check, so it is the honest last resort.
  return { tx: ox, ty: farm.ty };
}
